import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/reducers';
import { getInstallmentsByPaymentCard } from '../../../store/actions/installmentActions';
import {
    addInstallments,
    cancelEnrollment,
    evasaoEnrollment,
    reactivateEnrollment,
    renegotiateInstallments
} from '../../../store/actions/enrollmentActions';
import {
    Box,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Grid,
    Chip,
    InputAdornment,
    Divider,
    FormControlLabel,
    Switch,
    Alert
} from '@mui/material';
import MUIDataTable, { MUIDataTableColumnDef } from 'mui-datatables';
import { useSnackbar } from 'notistack';
import { format, parseISO, isValid } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import BlockIcon from '@mui/icons-material/Block';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import RefreshIcon from '@mui/icons-material/Refresh';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AddCircleIcon from '@mui/icons-material/AddCircle';

const formatBRL = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    try {
        const parsed = parseISO(dateStr);
        return isValid(parsed) ? format(parsed, 'dd/MM/yyyy') : '-';
    } catch {
        return '-';
    }
};

const formatDateTime = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    try {
        const parsed = parseISO(dateStr);
        return isValid(parsed) ? format(parsed, 'dd/MM/yyyy HH:mm') : '-';
    } catch {
        return '-';
    }
};

const getEnrollmentStatusLabel = (status: number): { label: string; color: 'success' | 'error' | 'warning' | 'default' } => {
    switch (status) {
        case 1: return { label: 'Ativa', color: 'success' };
        case 2: return { label: 'Cancelada', color: 'error' };
        case 3: return { label: 'Evasão', color: 'warning' };
        default: return { label: 'Desconhecido', color: 'default' };
    }
};

interface RenegotiateForm {
    fromNumber: string;
    toNumber: string;
    newAmount: string;
    zeroNextOnes: boolean;
}

interface AddInstallmentsForm {
    amount: string;
    count: string;
    firstDueDate: string;
}

const FinancialByPaymentCard: React.FC = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const installments = useSelector((state: RootState) => state.installments.installments);
    const enrollment = useSelector((state: RootState) => state.installments.enrollment);
    const student = useSelector((state: RootState) => state.installments.student);
    const isFetching = useSelector((state: RootState) => state.installments.isFetching);

    const [searchValue, setSearchValue] = useState('');
    const [searched, setSearched] = useState(false);

    // Dialog states
    const [renegotiateOpen, setRenegotiateOpen] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [evasaoOpen, setEvasaoOpen] = useState(false);
    const [reactivateOpen, setReactivateOpen] = useState(false);
    const [addInstallmentsOpen, setAddInstallmentsOpen] = useState(false);

    const [renegotiateForm, setRenegotiateForm] = useState<RenegotiateForm>({
        fromNumber: '',
        toNumber: '',
        newAmount: '',
        zeroNextOnes: false,
    });
    const [addInstallmentsForm, setAddInstallmentsForm] = useState<AddInstallmentsForm>({
        amount: '',
        count: '',
        firstDueDate: '',
    });

    const handleSearch = () => {
        if (!searchValue.trim()) {
            enqueueSnackbar('Informe o número do carnê', { variant: 'warning' });
            return;
        }
        // @ts-ignore
        dispatch(getInstallmentsByPaymentCard(searchValue.trim()));
        setSearched(true);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSearch();
    };

    const enrollmentId = enrollment?.id;
    const paymentCard = enrollment?.PaymentCard || searchValue;
    const personName = student?.Name || '-';
    const cpf = student?.IndividualIdentity || '-';
    const enrollmentStatus = enrollment?.Status ?? 1;
    const dhDate = enrollment?.dhDate;
    const dhExpireDate = enrollment?.dhExpireDate;

    const refreshData = () => {
        if (searchValue.trim()) {
            // @ts-ignore
            dispatch(getInstallmentsByPaymentCard(searchValue.trim()));
        }
    };

    const handleCancelEnrollment = () => {
        if (enrollmentId) {
            // @ts-ignore
            dispatch(cancelEnrollment(enrollmentId, searchValue.trim()));
            enqueueSnackbar('Matrícula cancelada com sucesso', { variant: 'success' });
            setCancelOpen(false);
        }
    };

    const handleEvasaoEnrollment = () => {
        if (enrollmentId) {
            // @ts-ignore
            dispatch(evasaoEnrollment(enrollmentId, searchValue.trim()));
            enqueueSnackbar('Evasão registrada com sucesso', { variant: 'success' });
            setEvasaoOpen(false);
        }
    };

    const handleReactivateEnrollment = () => {
        if (enrollmentId) {
            // @ts-ignore
            dispatch(reactivateEnrollment(enrollmentId, searchValue.trim()));
            enqueueSnackbar('Matrícula reativada com sucesso', { variant: 'success' });
            setReactivateOpen(false);
        }
    };

    const handleRenegotiate = () => {
        if (!enrollmentId) return;

        const fromNumber = parseInt(renegotiateForm.fromNumber);
        const toNumber = parseInt(renegotiateForm.toNumber);
        const newAmount = parseFloat(renegotiateForm.newAmount);

        if (isNaN(fromNumber) || isNaN(toNumber) || isNaN(newAmount)) {
            enqueueSnackbar('Preencha todos os campos corretamente', { variant: 'error' });
            return;
        }
        if (fromNumber > toNumber) {
            enqueueSnackbar('A parcela inicial não pode ser maior que a final', { variant: 'error' });
            return;
        }
        if (newAmount <= 0) {
            enqueueSnackbar('O novo valor deve ser maior que zero', { variant: 'error' });
            return;
        }

        // @ts-ignore
        dispatch(renegotiateInstallments(enrollmentId, {
            fromNumber,
            toNumber,
            newAmount,
            zeroNextOnes: renegotiateForm.zeroNextOnes,
        }, searchValue.trim()));

        enqueueSnackbar('Renegociação realizada com sucesso', { variant: 'success' });
        setRenegotiateOpen(false);
        setRenegotiateForm({ fromNumber: '', toNumber: '', newAmount: '', zeroNextOnes: false });
    };

    const handleAddInstallments = () => {
        if (!enrollmentId) return;

        const amount = parseFloat(addInstallmentsForm.amount);
        const count = parseInt(addInstallmentsForm.count);
        const firstDueDate = addInstallmentsForm.firstDueDate;

        if (isNaN(amount) || amount <= 0) {
            enqueueSnackbar('Informe um valor de parcela maior que zero', { variant: 'error' });
            return;
        }
        if (isNaN(count) || count <= 0) {
            enqueueSnackbar('Informe um numero de parcelas maior que zero', { variant: 'error' });
            return;
        }
        if (!firstDueDate) {
            enqueueSnackbar('Informe a data do primeiro vencimento', { variant: 'error' });
            return;
        }

        // @ts-ignore
        const promise: any = dispatch(addInstallments(enrollmentId, { amount, count, firstDueDate }, searchValue.trim()));
        promise
            .then(() => {
                enqueueSnackbar(`${count} parcela(s) adicionada(s) com sucesso`, { variant: 'success' });
                setAddInstallmentsOpen(false);
                setAddInstallmentsForm({ amount: '', count: '', firstDueDate: '' });
            })
            .catch(() => {
                enqueueSnackbar('Erro ao adicionar parcelas', { variant: 'error' });
            });
    };

    const lastInstallmentNumber = installments && installments.length > 0
        ? Math.max(...installments.map((p: any) => Number(p.NumberInEnrollment) || 0))
        : 0;

    const statusInfo = searched && enrollment ? getEnrollmentStatusLabel(enrollmentStatus) : null;

    const columns: MUIDataTableColumnDef[] = [
        {
            name: 'NumberInEnrollment',
            label: '#',
            options: { filter: false, sort: true }
        },
        {
            name: 'DueDate',
            label: 'Vencto',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: string) => formatDate(value)
            }
        },
        {
            name: 'Amount',
            label: 'Valor',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: number) => formatBRL(value || 0)
            }
        },
        {
            name: 'PayDate',
            label: 'Data Pagto',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: string) => formatDateTime(value)
            }
        },
        {
            name: 'CashCycle_id',
            label: 'Ciclo',
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value: number) => value || '-'
            }
        },
        {
            name: 'MultaJuros',
            label: 'Multa+Juros',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex: number) => {
                    const row = installments[dataIndex];
                    const mj = (row?.Interest || 0) + (row?.Fee || 0);
                    return mj > 0 ? formatBRL(mj) : '-';
                }
            }
        },
        {
            name: 'Discount',
            label: 'Desconto',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value: number) => value ? formatBRL(value) : '-'
            }
        },
        {
            name: 'TotalCalc',
            label: 'Total',
            options: {
                filter: false,
                sort: true,
                customBodyRenderLite: (dataIndex: number) => {
                    const row = installments[dataIndex];
                    const amount = row?.Amount || 0;
                    const discount = row?.Discount || 0;
                    const interest = row?.Interest || 0;
                    const fee = row?.Fee || 0;
                    const total = amount - discount + interest + fee;
                    return formatBRL(total);
                }
            }
        },
        {
            name: 'TotalPayment',
            label: 'Pago',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: number) => value ? formatBRL(value) : '-'
            }
        },
        {
            name: 'Saldo',
            label: 'Saldo',
            options: {
                filter: false,
                sort: true,
                customBodyRenderLite: (dataIndex: number) => {
                    const row = installments[dataIndex];
                    const amount = row?.Amount || 0;
                    const discount = row?.Discount || 0;
                    const interest = row?.Interest || 0;
                    const fee = row?.Fee || 0;
                    const total = amount - discount + interest + fee;
                    const paid = row?.TotalPayment || 0;
                    const saldo = total - paid;
                    return (
                        <Typography
                            variant="body2"
                            color={saldo > 0 ? 'error' : 'success.main'}
                            fontWeight={saldo > 0 ? 'bold' : 'normal'}
                        >
                            {formatBRL(Math.max(saldo, 0))}
                        </Typography>
                    );
                }
            }
        },
        {
            name: 'Comment',
            label: 'Comentário',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value: string) => value || '-'
            }
        },
    ];

    const tableOptions = {
        filterType: 'dropdown' as const,
        responsive: 'standard' as const,
        selectableRows: 'none' as const,
        rowsPerPage: 50,
        rowsPerPageOptions: [15, 30, 50],
        download: false,
        print: false,
        textLabels: {
            body: {
                noMatch: searched ? 'Nenhuma parcela encontrada para este carnê' : 'Pesquise um número de carnê',
                toolTip: 'Ordenar',
            },
            pagination: {
                next: 'Próxima Página',
                previous: 'Página Anterior',
                rowsPerPage: 'Registros por página:',
                displayRows: 'de',
            },
            toolbar: {
                search: 'Pesquisar',
                viewColumns: 'Ver colunas',
                filterTable: 'Filtrar',
            },
            filter: {
                all: 'Todos',
                title: 'Filtros',
                reset: 'Reiniciar',
            },
        },
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Operações com Matrícula</Typography>
                <Box />
            </Box>

            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        label="Número do Carnê"
                        variant="outlined"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        size="small"
                        sx={{ minWidth: 250 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button variant="contained" onClick={handleSearch} disabled={isFetching}>
                        Buscar
                    </Button>
                </Box>
            </Paper>

            {searched && enrollment && (
                <>
                    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                                <Typography variant="body2" color="text.secondary">Aluno</Typography>
                                <Typography variant="body1" fontWeight="bold">{personName}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <Typography variant="body2" color="text.secondary">CPF</Typography>
                                <Typography variant="body1">{cpf}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <Typography variant="body2" color="text.secondary">Carnê</Typography>
                                <Typography variant="body1">{paymentCard}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <Typography variant="body2" color="text.secondary">Data Matrícula</Typography>
                                <Typography variant="body1">{formatDate(dhDate)}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={1}>
                                <Typography variant="body2" color="text.secondary">Expira</Typography>
                                <Typography variant="body1">{formatDate(dhExpireDate)}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <Typography variant="body2" color="text.secondary">Status</Typography>
                                {statusInfo && <Chip label={statusInfo.label} color={statusInfo.color} />}
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<HandshakeIcon />}
                                onClick={() => setRenegotiateOpen(true)}
                            >
                                Renegociar
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<AddCircleIcon />}
                                onClick={() => setAddInstallmentsOpen(true)}
                            >
                                Adicionar Parcelas
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<BlockIcon />}
                                onClick={() => setCancelOpen(true)}
                                disabled={enrollmentStatus === 2}
                            >
                                Cancelar Matrícula
                            </Button>
                            <Button
                                variant="outlined"
                                color="warning"
                                startIcon={<ExitToAppIcon />}
                                onClick={() => setEvasaoOpen(true)}
                                disabled={enrollmentStatus === 3}
                            >
                                Evasão
                            </Button>
                            <Button
                                variant="outlined"
                                color="success"
                                startIcon={<RefreshIcon />}
                                onClick={() => setReactivateOpen(true)}
                                disabled={enrollmentStatus === 1}
                            >
                                Reativar
                            </Button>
                        </Box>
                    </Paper>
                </>
            )}

            {isFetching ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper elevation={2}>
                    <MUIDataTable
                        title="Parcelas"
                        data={installments}
                        columns={columns}
                        options={tableOptions}
                    />
                </Paper>
            )}

            {/* Renegotiate Dialog */}
            <Dialog open={renegotiateOpen} onClose={() => setRenegotiateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Renegociar Parcelas</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
                        Informe o intervalo de parcelas e o novo valor para renegociação.
                    </Alert>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                label="Mês Inicial #"
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={renegotiateForm.fromNumber}
                                onChange={(e) => setRenegotiateForm({ ...renegotiateForm, fromNumber: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Mês Final #"
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={renegotiateForm.toNumber}
                                onChange={(e) => setRenegotiateForm({ ...renegotiateForm, toNumber: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Novo Valor Mensal (R$)"
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={renegotiateForm.newAmount}
                                onChange={(e) => setRenegotiateForm({ ...renegotiateForm, newAmount: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={renegotiateForm.zeroNextOnes}
                                        onChange={(e) => setRenegotiateForm({ ...renegotiateForm, zeroNextOnes: e.target.checked })}
                                    />
                                }
                                label="Zerar demais parcelas"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRenegotiateOpen(false)}>Cancelar</Button>
                    <Button onClick={handleRenegotiate} variant="contained">
                        Confirmar Renegociação
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Installments Dialog */}
            <Dialog open={addInstallmentsOpen} onClose={() => setAddInstallmentsOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Adicionar Parcelas</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
                        Novas parcelas serao numeradas a partir de #{lastInstallmentNumber + 1}, vencendo a cada mes a partir da data informada.
                    </Alert>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                label="Valor da Parcela (R$)"
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={addInstallmentsForm.amount}
                                onChange={(e) => setAddInstallmentsForm({ ...addInstallmentsForm, amount: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Numero de Parcelas Adicionais"
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={addInstallmentsForm.count}
                                onChange={(e) => setAddInstallmentsForm({ ...addInstallmentsForm, count: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Data do 1o Vencimento"
                                type="date"
                                fullWidth
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                value={addInstallmentsForm.firstDueDate}
                                onChange={(e) => setAddInstallmentsForm({ ...addInstallmentsForm, firstDueDate: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddInstallmentsOpen(false)}>Cancelar</Button>
                    <Button onClick={handleAddInstallments} variant="contained">
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancel Enrollment Dialog */}
            <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Cancelar Matrícula</DialogTitle>
                <DialogContent>
                    <Alert severity="error" sx={{ mt: 1 }}>
                        Tem certeza que deseja cancelar a matrícula do aluno <strong>{personName}</strong> (Carnê {paymentCard})?
                    </Alert>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        O status da matrícula será alterado para "Cancelada".
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelOpen(false)}>Voltar</Button>
                    <Button onClick={handleCancelEnrollment} variant="contained" color="error">
                        Confirmar Cancelamento
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Evasao Dialog */}
            <Dialog open={evasaoOpen} onClose={() => setEvasaoOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Registrar Evasão</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        Tem certeza que deseja registrar evasão para o aluno <strong>{personName}</strong> (Carnê {paymentCard})?
                    </Alert>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        O status da matrícula será alterado para "Evasão".
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEvasaoOpen(false)}>Voltar</Button>
                    <Button onClick={handleEvasaoEnrollment} variant="contained" color="warning">
                        Confirmar Evasão
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reactivate Dialog */}
            <Dialog open={reactivateOpen} onClose={() => setReactivateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Reativar Matrícula</DialogTitle>
                <DialogContent>
                    <Alert severity="success" sx={{ mt: 1 }}>
                        Tem certeza que deseja reativar a matrícula do aluno <strong>{personName}</strong> (Carnê {paymentCard})?
                    </Alert>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        O status da matrícula será alterado para "Ativa".
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReactivateOpen(false)}>Voltar</Button>
                    <Button onClick={handleReactivateEnrollment} variant="contained" color="success">
                        Confirmar Reativação
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FinancialByPaymentCard;
